import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { contractsApi, signaturesApi, getFullApiUrl } from '../services/api';
import { Contract, FormattedSection, SignatureField } from '../types';
import { useAuth } from '../context/AuthContext';
import SignedDocuments from '../components/SignedDocuments';
import {
  ArrowLeft,
  Edit,
  Download,
  Send,
  PenTool,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Eye,
  Plus,
  X,
  Calendar
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string; description: string }> = {
  DRAFT: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700', description: 'Vertrag wird bearbeitet' },
  SAVED: { label: 'Gespeichert', color: 'bg-yellow-100 text-yellow-700', description: 'Zwischengespeichert' },
  FINALIZED: { label: 'Finalisiert', color: 'bg-blue-100 text-blue-700', description: 'Bereit zur Freigabe' },
  PENDING_REVIEW: { label: 'In Prüfung', color: 'bg-purple-100 text-purple-700', description: 'Wartet auf Überprüfung' },
  PENDING_SIGNATURE: { label: 'Zur Unterschrift', color: 'bg-orange-100 text-orange-700', description: 'Wartet auf Unterschriften' },
  SIGNED: { label: 'Unterschrieben', color: 'bg-green-100 text-green-700', description: 'Vollständig unterschrieben' },
  ARCHIVED: { label: 'Archiviert', color: 'bg-gray-100 text-gray-500', description: 'Im Archiv' }
};

export default function ContractView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth(); // For auth context
  const [contract, setContract] = useState<Contract | null>(null);
  const [formattedSections, setFormattedSections] = useState<FormattedSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedSignatureField, setSelectedSignatureField] = useState<SignatureField | null>(null);
  const [showAddSignatureField, setShowAddSignatureField] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadContract = async () => {
    try {
      const [contractRes, formattedRes] = await Promise.all([
        contractsApi.getById(id!),
        contractsApi.getFormatted(id!)
      ]);
      setContract(contractRes.contract);
      setFormattedSections(formattedRes.formattedSections);
    } catch (err) {
      setError('Fehler beim Laden des Vertrags');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (action: 'review' | 'signature') => {
    setIsProcessing(true);
    setError('');

    try {
      if (action === 'review') {
        await contractsApi.submitReview(id!);
        setSuccessMessage('Vertrag zur Überprüfung freigegeben');
      } else {
        await contractsApi.submitSignature(id!);
        setSuccessMessage('Vertrag zur Unterschrift freigegeben');
      }
      await loadContract();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Aktion fehlgeschlagen');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddSignatureField = async (label: string) => {
    setIsProcessing(true);
    try {
      const existingFields = contract?.signatureFields || [];
      await contractsApi.updateSignatureFields(id!, [
        ...existingFields.map(f => ({
          label: f.label,
          required: f.required,
          positionX: f.positionX,
          positionY: f.positionY,
          pageNumber: f.pageNumber,
          width: f.width,
          height: f.height
        })),
        {
          label,
          required: true,
          positionX: 10,
          positionY: 80 + existingFields.length * 15,
          pageNumber: 1,
          width: 200,
          height: 80
        }
      ]);
      await loadContract();
      setShowAddSignatureField(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Hinzufügen');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSign = (field: SignatureField) => {
    setSelectedSignatureField(field);
    setShowSignatureModal(true);
  };

  const status = contract ? statusConfig[contract.status] : null;
  const canEdit = contract && ['DRAFT', 'SAVED'].includes(contract.status);
  const canAddSignatureFields = contract && ['DRAFT', 'SAVED', 'FINALIZED', 'PENDING_REVIEW'].includes(contract.status);
  const canSubmitReview = contract?.status === 'FINALIZED';
  const canSubmitSignature = contract && ['FINALIZED', 'PENDING_REVIEW'].includes(contract.status);
  const canSign = contract?.status === 'PENDING_SIGNATURE';
  const isDraft = contract && ['DRAFT', 'SAVED', 'PENDING_REVIEW'].includes(contract.status);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900">Vertrag nicht gefunden</h2>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-4 sm:-mx-6 lg:-mx-8 -mt-8 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/contracts')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <div className="flex items-center">
                  <h1 className="text-xl font-bold text-gray-900">{contract.title}</h1>
                  <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${status?.color}`}>
                    {status?.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{status?.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {canEdit && (
                <Link
                  to={`/contracts/${id}/edit`}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Bearbeiten
                </Link>
              )}
              <a
                href={getFullApiUrl(`/pdf/${id}?download=true`, true)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </a>
              {canSubmitReview && (
                <button
                  onClick={() => handleStatusChange('review')}
                  disabled={isProcessing}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Zur Prüfung
                </button>
              )}
              {canSubmitSignature && (
                <button
                  onClick={() => handleStatusChange('signature')}
                  disabled={isProcessing || (contract.signatureFields?.length || 0) === 0}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Zur Unterschrift
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <CheckCircle className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contract Content */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 relative">
            {/* Draft Watermark */}
            {isDraft && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                <span className="text-8xl font-bold text-gray-100 transform -rotate-45 select-none">
                  ENTWURF
                </span>
              </div>
            )}

            {/* Contract Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
              {contract.title}
            </h2>

            {/* Formatted Sections with § numbering */}
            <div className="space-y-8 relative z-10">
              {formattedSections.map((section) => (
                <div key={section.sectionId}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {section.title}
                  </h3>
                  {section.blocks.map((block) => (
                    <div key={block.id} className="mb-4">
                      <div
                        className="text-gray-700 whitespace-pre-wrap leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: block.content.replace(
                            /\[([^\]]+)\]/g,
                            '<span class="text-red-500 italic">[$1]</span>'
                          )
                        }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {formattedSections.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Keine Inhalte vorhanden</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contract Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-medium text-gray-900 mb-4">Vertragsinformationen</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Erstellt am</dt>
                <dd className="text-gray-900">{new Date(contract.createdAt).toLocaleDateString('de-AT')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Erstellt von</dt>
                <dd className="text-gray-900">
                  {contract.createdBy?.firstName} {contract.createdBy?.lastName}
                </dd>
              </div>
              {contract.template && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Vorlage</dt>
                  <dd className="text-gray-900">{contract.template.name}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Bauteile</dt>
                <dd className="text-gray-900">{contract._count?.blocks || contract.blocks?.length || 0}</dd>
              </div>
            </dl>
          </div>

          {/* Signature Fields */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Unterschriftsfelder</h3>
              {canAddSignatureFields && (
                <button
                  onClick={() => setShowAddSignatureField(true)}
                  className="p-1.5 text-primary-600 hover:bg-primary-50 rounded"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>

            {contract.signatureFields && contract.signatureFields.length > 0 ? (
              <div className="space-y-3">
                {contract.signatureFields.map((field) => (
                  <div
                    key={field.id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{field.label}</span>
                      {field.required && (
                        <span className="text-xs text-red-500">Erforderlich</span>
                      )}
                    </div>
                    {field.signature ? (
                      <div className="flex items-center text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span>
                          Unterschrieben von {field.signature.signedBy?.firstName}{' '}
                          {field.signature.signedBy?.lastName}
                        </span>
                      </div>
                    ) : canSign ? (
                      <button
                        onClick={() => handleSign(field)}
                        className="w-full py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
                      >
                        <PenTool className="h-4 w-4 inline mr-2" />
                        Jetzt unterschreiben
                      </button>
                    ) : (
                      <div className="flex items-center text-gray-500 text-sm">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>Ausstehend</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Keine Unterschriftsfelder definiert
              </p>
            )}

            {/* QES Info */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
              <strong>Hinweis zur Rechtsgültigkeit in Österreich:</strong>
              <br />
              Für eine qualifizierte elektronische Signatur (QES) nach eIDAS-Verordnung
              ist die Integration mit einem Vertrauensdiensteanbieter wie A-Trust erforderlich.
            </div>
          </div>

          {/* Expiration Info */}
          {(contract.expirationDate || contract.isUnlimited) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Befristung
              </h3>
              {contract.isUnlimited ? (
                <p className="text-sm text-gray-600">
                  Dieser Vertrag ist <span className="font-medium text-green-600">unbefristet</span>.
                </p>
              ) : contract.expirationDate && (
                <div>
                  <p className="text-sm text-gray-600">
                    Läuft ab am:{' '}
                    <span className="font-medium text-gray-900">
                      {new Date(contract.expirationDate).toLocaleDateString('de-AT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </p>
                  {(() => {
                    const now = new Date();
                    const exp = new Date(contract.expirationDate);
                    const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (diffDays < 0) {
                      return (
                        <p className="mt-2 p-2 bg-red-100 text-red-700 rounded text-sm font-medium">
                          Vertrag ist abgelaufen!
                        </p>
                      );
                    } else if (diffDays <= 30) {
                      return (
                        <p className="mt-2 p-2 bg-red-100 text-red-700 rounded text-sm font-medium">
                          Läuft in {diffDays} Tagen ab!
                        </p>
                      );
                    } else if (diffDays <= 60) {
                      return (
                        <p className="mt-2 p-2 bg-yellow-100 text-yellow-700 rounded text-sm">
                          Noch {diffDays} Tage gültig
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Signed Documents */}
          <SignedDocuments 
            contractId={id!} 
            canUpload={['SIGNED', 'PENDING_SIGNATURE'].includes(contract.status)}
          />
        </div>
      </div>

      {/* Add Signature Field Modal */}
      {showAddSignatureField && (
        <AddSignatureFieldModal
          onClose={() => setShowAddSignatureField(false)}
          onAdd={handleAddSignatureField}
          isLoading={isProcessing}
        />
      )}

      {/* Signature Modal */}
      {showSignatureModal && selectedSignatureField && (
        <SignatureModal
          field={selectedSignatureField}
          contractId={id!}
          onClose={() => {
            setShowSignatureModal(false);
            setSelectedSignatureField(null);
          }}
          onSigned={() => {
            setShowSignatureModal(false);
            setSelectedSignatureField(null);
            loadContract();
            setSuccessMessage('Unterschrift erfolgreich gespeichert');
          }}
        />
      )}
    </div>
  );
}

// Add Signature Field Modal
function AddSignatureFieldModal({
  onClose,
  onAdd,
  isLoading
}: {
  onClose: () => void;
  onAdd: (label: string) => void;
  isLoading: boolean;
}) {
  const [label, setLabel] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (label.trim()) {
      onAdd(label.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Unterschriftsfeld hinzufügen
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezeichnung
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Vermieter, Mieter, Zeuge"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading || !label.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Hinzufügen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Signature Modal Component
function SignatureModal({
  field,
  contractId,
  onClose,
  onSigned
}: {
  field: SignatureField;
  contractId: string;
  onClose: () => void;
  onSigned: () => void;
}) {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleClear = () => {
    sigCanvas.current?.clear();
  };

  const handleSubmit = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setError('Bitte unterschreiben Sie zuerst');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      await signaturesApi.sign({
        signatureFieldId: field.id,
        signatureData,
        signatureType: 'HANDWRITTEN'
      });
      onSigned();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unterschrift fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Unterschrift: {field.label}
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Bitte unterschreiben Sie im Feld unten:
            </p>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  width: 450,
                  height: 200,
                  className: 'signature-canvas'
                }}
                backgroundColor="white"
              />
            </div>
          </div>

          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              <strong>Rechtlicher Hinweis:</strong> Mit Ihrer Unterschrift bestätigen Sie, 
              dass Sie den Vertrag gelesen haben und mit den Bedingungen einverstanden sind.
              Diese digitale Unterschrift ist nach österreichischem Recht eine einfache 
              elektronische Signatur. Für eine qualifizierte elektronische Signatur (QES) 
              wäre eine Integration mit A-Trust oder einem vergleichbaren Dienst erforderlich.
            </p>
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleClear}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Löschen
            </button>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Wird gespeichert...' : 'Unterschreiben'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
